import dayjs from "dayjs";
import "dayjs/locale/sv";
import { router } from "expo-router";
import { observer } from "mobx-react";
import React, { useCallback, useState } from "react";
import { Pressable } from "~/components/V2/Pressable";
import { BodyText } from "~/components/V2/Text/BodyText";
import { useStandaloneBookingFlow } from "~/features/book-appointment/useStandaloneBookingFlow";
import type { TimeslotInstance } from "~/mobx/entities/timeslots/Timeslot";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { View } from "./View";
import type { PressableStateCallbackType } from "react-native";

export const TimeslotChip = observer(function TimeslotChip({
  timeslot,
  clinicTimezone,
}: {
  timeslot: TimeslotInstance;
  clinicTimezone: string;
}) {
  const { clinicId, isStandAloneBookingFlow, isNewUser } =
    useStandaloneBookingFlow();
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const store = useStore();

  const resolveBackgroundColor = useCallback(
    (state: PressableStateCallbackType) => {
      if (state.pressed) {
        return styleConstantsV2.colorPrimary;
      }
      if (state.hovered) {
        return styleConstantsV2.colorPrimary;
      }

      return styleConstantsV2.colorPrimaryBg;
    },
    [],
  );

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      flex
      paddingHorizontal16
      paddingVertical8
      centerContent
      style={(state) => ({
        backgroundColor: resolveBackgroundColor(state),
        height: 36,
        borderRadius: styleConstantsV2.borderRadius8,
      })}
      onPress={() => {
        store.bookingStore.setTimeslot(
          timeslot.startUtc,
          timeslot.endUtc,
          timeslot.practitioner.id,
        );

        if (isStandAloneBookingFlow) {
          //Log the event to the booking data layer
          bookingDataLayerPush({
            event: "Booking_web_app",
            ga_action: "Choose time & date",
          });

          router.navigate({
            pathname: "/(standalone)/booking/notes",
            params: {
              clinicId,
              isNewUser: isNewUser ? "1" : "0",
            },
          });
        } else {
          router.navigate({
            pathname: "/(app)/book-appointment/notes",
          });
        }
      }}
    >
      <BodyText
        weightBold
        colorBlack={isHovered === false && isPressed === false}
        colorWhite={isHovered === true || isPressed === true}
        style={{ fontSize: 14, lineHeight: 0 }}
      >
        {dayjs(timeslot.startUtc).tz(clinicTimezone).format("HH:mm")}
      </BodyText>
    </Pressable>
  );
});

export const EmptyTimeslot = observer(function EmptyTimeslot() {
  return (
    <View flex centerContent paddingHorizontal16 paddingVertical8>
      <BodyText
        weightBold
        colorBlack
        style={{ fontSize: 14, lineHeight: 0, display: "none" }}
      >
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {"XX:XX"}
      </BodyText>
    </View>
  );
});
